import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ValidatedExpeditionModifyChoice } from "./modifyValidatedExpedition.type";
import { useCancelExpedition } from "@/api/expedition.api";
import { InfoIcon, Loader2 } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import Paths from "@/constants/paths";

type ApiEnum = "FEDEX" | "NONE";

type ModifyOption = {
  value: ValidatedExpeditionModifyChoice;
  label: string;
  apiEnumAllowed: ApiEnum[];
  tooltip: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  expeditionId: string;
};

export const ModifyValidatedExpeditionModal = ({ open, onClose, expeditionId }: Props) => {
  const { t } = useTranslation(["boExpeditions", "common"]);
  const navigate = useNavigate();
  const id = useId();

  const modifyOptions = useMemo<ModifyOption[]>(
    () => [
      {
        value: "CANCEL",
        label: t("modifyValidatedExpeditionModal.cancelOptionLabel"),
        apiEnumAllowed: ["FEDEX"],
        tooltip: t("modifyValidatedExpeditionModal.cancelOptionTooltip"),
      },
      {
        value: "MANUAL_ONLY",
        label: t("modifyValidatedExpeditionModal.manualOnlyOptionLabel"),
        apiEnumAllowed: ["NONE", "FEDEX"],
        tooltip: t("modifyValidatedExpeditionModal.manualOnlyOptionTooltip"),
      },
    ],
    [t],
  );
  const { mutateAsync: cancelExpedition, isPending: isCancelling } = useCancelExpedition();

  const [selected, setSelected] = useState<ValidatedExpeditionModifyChoice | undefined>(undefined);

  useEffect(() => {
    if (!open) setSelected(undefined);
  }, [open]);

  const handleConfirm = async () => {
    if (!selected) return;
    const apiEnum = selected === "CANCEL" ? "FEDEX" : "NONE";
    if (selected === "CANCEL") {
      await cancelExpedition({ id: expeditionId });
    }
    onClose();
    navigate(Paths.bo.expeditionForm.replace(":id", expeditionId), {
      state: {
        apiEnum,
        initialStep: 1,
        modifyMode: selected === "MANUAL_ONLY" ? "MANUAL_ONLY" : undefined,
      },
    });
    setSelected(undefined);
  };

  const handleClose = () => {
    setSelected(undefined);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("modifyValidatedExpeditionModal.title")}
      description={t("modifyValidatedExpeditionModal.description")}
      size="md"
      contentClassName="max-w-[560px]"
      footer={
        <div className="flex w-full flex-col items-center gap-2">
          <Button disabled={!selected || isCancelling} className="w-full" onClick={handleConfirm}>
            {t("common:actions.confirm")}
          </Button>
          <Button
            disabled={isCancelling}
            className="w-full"
            variant="outline"
            onClick={handleClose}
          >
            {t("common:actions.cancel")}
          </Button>
        </div>
      }
    >
      {isCancelling ? (
        <div className="flex w-full justify-center py-8">
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <RadioGroup
          name={`modifyValidatedExpedition-${expeditionId}`}
          value={selected}
          onValueChange={(v) => setSelected(v as ValidatedExpeditionModifyChoice)}
          aria-label={t("modifyValidatedExpeditionModal.modeAriaLabel")}
          className="flex flex-col gap-2"
        >
          {modifyOptions.map(({ value, label, tooltip }) => {
            const inputId = `${id}-${value}`;
            const isSelected = selected === value;

            return (
              <Label
                key={value}
                htmlFor={inputId}
                className={cn(
                  "flex min-h-14 cursor-pointer items-center gap-2 rounded-lg border px-4 py-3",
                  isSelected ? "border-primary bg-primary/5" : "border-gray-200",
                )}
              >
                <RadioGroupItem value={value} id={inputId} className="shrink-0" />
                <span className="text-sm leading-snug">{label}</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <InfoIcon className="h-4 w-4" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-64 text-sm">{tooltip}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Label>
            );
          })}
        </RadioGroup>
      )}
    </Modal>
  );
};
